import { settings } from '@/_config';
import type { TFinalPricingModelData } from '@/_models';
import { finalPricingModelData } from '@/_models';
import { dateTime, safeFloatParse, ternary } from '@/_shared';

import { areSplitPaymentsValid, getFloatInput } from '../helper';

import { couponCalculations } from './coupon-price';
import { getInvestment } from './investment';
import type { IInstallmentProps } from './types';

const yesterday = dateTime.now().subtract(1, 'day');
const sevenDays = yesterday.add(8, 'days');

export function getInstallmentPrice({
  success,
  totalSelectedPrepCourses,
  totalSelectedOdtCourses,
  totalSelectedFundamentalCourses,
  totalSelectedCourses,
  prepPriceRanges,
  odtPriceRanges,
  fundamentalPriceRanges,
  preBuyInfo,
  paymentOptions,
  modelType,
  coupons,
  account_charge,
  isReviseMode,
  isClimb,
}: IInstallmentProps): TFinalPricingModelData {
  if (!success || totalSelectedCourses === 0) {
    return finalPricingModelData;
  }
  const {
    isMatchupShown,
    discountPercentage: tempDiscountPercentage,
    minDownPayment,
    wantToPayDownPayment,
    paymentTerm,
    subscriptionType,
    recurringPaymentStartDate,
    splitPayments,
  } = paymentOptions;

  let matchedPrice = 0;
  let isMatchApplicable = false;
  const discountPercentage = isClimb ? 0 : tempDiscountPercentage;

  const hasSplitDownPayment = areSplitPaymentsValid(splitPayments);

  const floatInput = getFloatInput(hasSplitDownPayment, splitPayments, wantToPayDownPayment);

  const withSplitDownPayment = safeFloatParse(floatInput);
  const matchOnlyDownPayment = safeFloatParse(wantToPayDownPayment);

  const matching = hasSplitDownPayment
    ? splitPayments.reduce(
        (acc, cur) =>
          acc +
          (dateTime.isInBetween(cur.date, { startTime: yesterday, endTime: sevenDays })
            ? safeFloatParse(cur.amount)
            : 0),
        0
      )
    : safeFloatParse(wantToPayDownPayment);

  // Matching logic
  // minCourseToMatchup(3) course check
  if (totalSelectedCourses >= settings.bridgePlan.minCourseToMatchup) {
    // logic change
    // https://app.asana.com/0/1202245529482419/1206711691457872/f

    const minMatchingAmount =
      modelType === 'UNIFIED_PRICING_4' ? settings.bridgePlan.minMatchupAmount_4 : settings.bridgePlan.minMatchupAmount;

    if (matching >= minMatchingAmount && matching <= settings.bridgePlan.maxMatchupAmount) {
      matchedPrice = matching;
    } else if (matching > settings.bridgePlan.maxMatchupAmount) {
      matchedPrice = settings.bridgePlan.maxMatchupAmount;
    }

    if (matchedPrice > 0) {
      isMatchApplicable = true;
    }
  }

  matchedPrice = isMatchupShown ? matchedPrice : 0;

  const {
    totalInvestment,
    totalPrepCourseInvestment,
    totalOdtCourseInvestment,
    totalFundamentalCourseInvestment,
    perPrepCoursePrice,
    perOdtCoursePrice,
    perFundamentalCoursePrice,
  } = getInvestment({
    totalSelectedPrepCourses,
    totalSelectedOdtCourses,
    totalSelectedFundamentalCourses,
    totalSelectedCourses,

    prepPriceRanges,
    odtPriceRanges,
    fundamentalPriceRanges,
    modelType,
  });

  const discountedAmount = (totalInvestment * discountPercentage) / 100;
  const investmentAfterPercentageDiscountOnly = totalInvestment - discountedAmount + account_charge;

  const useDownPayment = ternary(
    isReviseMode,
    withSplitDownPayment,
    withSplitDownPayment >= minDownPayment ? withSplitDownPayment : minDownPayment
  );

  // down payment balance
  const downPaymentBalance = useDownPayment - (withSplitDownPayment || 0); // 325 - 300 => 25
  const hasDownPayedMoreAmount = downPaymentBalance < 0;

  const totalCouponValue = couponCalculations(coupons); // 100

  const remainingBalanceToCalcInstallments =
    investmentAfterPercentageDiscountOnly - totalCouponValue - matchedPrice - preBuyInfo - useDownPayment;

  const perInstallmentPrice = remainingBalanceToCalcInstallments / paymentTerm;

  const totalSavedAmount = matchedPrice + totalCouponValue + discountedAmount;
  const totalSavedAmountPercentage = (totalSavedAmount / totalInvestment) * 100;

  const perCourseDiscountedPrice = (perInstallmentPrice * paymentTerm + withSplitDownPayment) / totalSelectedCourses;

  const investmentAfterSavings = remainingBalanceToCalcInstallments + preBuyInfo + useDownPayment;

  let isPifBasedCommission = false;
  if (subscriptionType === 'monthly' && paymentTerm <= 6) {
    isPifBasedCommission = true;
  } else if (subscriptionType === 'bi-weekly' && paymentTerm <= 13) {
    isPifBasedCommission = true;
  }

  return {
    totalInvestment,
    totalPrepCourseInvestment,
    totalOdtCourseInvestment,
    totalFundamentalCourseInvestment,

    investmentAfterPercentageDiscountOnly,
    discountPercentage,

    perPrepCoursePrice,
    perOdtCoursePrice,
    perFundamentalCoursePrice,

    perCourseDiscountedPrice,

    discountedAmount,
    totalSavedAmount,
    totalSavedAmountPercentage,

    totalCouponValue,
    isMatchApplicable,
    matchedPrice,
    totalDiscountWithoutPaymentTerms: matchedPrice + totalCouponValue + discountedAmount,
    todaysPayment: matchOnlyDownPayment,

    hasDownPayedMoreAmount,
    downPaymentBalance,

    preBuyBridgePlanAmount: preBuyInfo,

    perInstallmentPrice,

    numberOfInstallments: paymentTerm,
    isPifBasedCommission,
    // finalTotalPayment,

    remainingBalanceToCalcInstallments,
    investmentAfterSavings,

    recurringPaymentStartDate,
    withSplitDownPayment: `${withSplitDownPayment}`,

    floatInput,
  };
}
